import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Save, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../ui/textarea';
import { usePlatform } from '../BrandProvider';
import { api } from '../../lib/api';
import { FOOTER_CONTACTS } from '../../content/legal';
import { LEGAL_TABS } from '../LegalPage';

function Area({ label, k, d, set, rows = 'h-72', hint }) {
  return (
    <label className="block space-y-1.5">
      <span className="t-label">{label}</span>
      <Textarea data-testid={`legal-edit-${k}`} value={d[k]} onChange={(e) => set({ ...d, [k]: e.target.value })} className={`${rows} bg-black/30 font-mono text-xs leading-5`} />
      {hint && <span className="block text-[10px] text-slate-500 t-chat">{hint}</span>}
    </label>
  );
}

export default function LegalEditor() {
  const { legal, setLegal } = usePlatform();
  const defaults = () => ({ ...Object.fromEntries(LEGAL_TABS.map((t) => [t.key, t.def.trim()])), footerContacts: FOOTER_CONTACTS });
  const [d, setD] = useState(() => { const df = defaults(); return { ...legal, ...Object.fromEntries(Object.keys(df).map((k) => [k, legal[k] || df[k]])) }; });
  const reset = () => { if (window.confirm('Ripristinare tutti i testi predefiniti? Le modifiche non salvate andranno perse.')) { setD({ ...d, ...defaults() }); toast('Testi predefiniti ripristinati: premi Salva e pubblica'); } };
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { setLegal(await api('/admin/legal', { method: 'PUT', body: d, auth: true })); toast.success('Testi legali pubblicati'); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const fmt = 'Formato: "# " titolo, "## " sezione, "- " elenco, "Etichetta: testo" in grassetto, riga vuota = nuovo paragrafo. Le email diventano link.';
  return (
    <div data-testid="admin-legal" className="space-y-5">
      {LEGAL_TABS.map((t) => <Area key={t.key} label={t.label === 'Termini' ? 'Termini di Servizio' : t.label === 'Cookie' ? 'Cookie e Archiviazione Locale' : t.label === 'Privacy' ? 'Privacy Policy (GDPR)' : 'Note Legali & Copyright'} k={t.key} d={d} set={setD} hint={fmt} />)}
      <Area label="Contatti nel footer dello Store" k="footerContacts" d={d} set={setD} rows="h-16" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Area label="Banner cookie (vuoto = nascosto)" k="cookieBannerText" d={d} set={setD} rows="h-24" />
        <Area label="Disclaimer nel footer" k="disclaimerText" d={d} set={setD} rows="h-24" />
      </div>
      <div data-testid="legal-updated-info" className="text-xs text-slate-400">Ultimo aggiornamento: <b className="text-slate-200">{legal.lastUpdatedDate || 'mai salvato'}</b> · si aggiorna da solo a ogni salvataggio</div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="legal-save-btn" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salva e pubblica modifiche
        </button>
        <button data-testid="legal-reset-btn" onClick={reset} className="flex items-center gap-1.5 rounded-full border border-amber-500/40 px-4 py-2.5 text-xs text-amber-200 hover:bg-amber-500/10"><RotateCcw className="h-3.5 w-3.5" />Ripristina testi predefiniti</button>
        <Link to="/legal/privacy" data-testid="legal-preview-privacy" className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2.5 text-xs text-slate-300 hover:bg-white/5"><ExternalLink className="h-3.5 w-3.5" />Vedi pagina /legal</Link>
      </div>
    </div>
  );
}
